import React from "react";
import { Link } from "react-router-dom";
import { Phone, MessageCircle, Mail, MapPin } from "lucide-react";
import { BRAND, CONTACT, LEGAL, SERVICES, SOCIAL, waLink } from "@/config/brand";

export const Footer = () => (
    <footer className="bg-[#001B3D] text-white/80 mt-16 pb-24 md:pb-16">
        <div className="container-ys pt-12 pb-8 grid grid-cols-1 md:grid-cols-4 gap-8">
            <div className="md:col-span-1">
                <div className="flex items-center gap-3">
                    <img
                        src={BRAND.logo}
                        alt={BRAND.logoAlt}
                        className="h-11 w-11 rounded-md object-contain"
                        width={44}
                        height={44}
                    />
                    <div className="text-white font-heading font-bold text-lg leading-tight">
                        {BRAND.name}
                        <div className="text-[10px] font-medium tracking-[0.18em] uppercase text-white/60 mt-0.5">Your Man With Van</div>
                    </div>
                </div>
                <p className="mt-3 text-sm leading-relaxed">
                    {BRAND.description}
                </p>
                <div className="mt-4 space-y-2 text-sm">
                    <a href={CONTACT.phoneHref} className="flex items-center gap-2 hover:text-white">
                        <Phone className="h-4 w-4" /> {CONTACT.phoneDisplay}
                    </a>
                    <a href={waLink()} target="_blank" rel="noreferrer" className="flex items-center gap-2 hover:text-white">
                        <MessageCircle className="h-4 w-4 text-[#25D366]" /> {CONTACT.whatsappDisplay}
                    </a>
                    <a href={CONTACT.emailHref} className="flex items-center gap-2 hover:text-white">
                        <Mail className="h-4 w-4" /> {CONTACT.email}
                    </a>
                    <div className="flex items-center gap-2">
                        <MapPin className="h-4 w-4" /> {CONTACT.addressLine1}, {CONTACT.addressLine2}
                    </div>
                </div>
            </div>

            <div>
                <div className="text-white font-semibold mb-3 text-sm uppercase tracking-wide">Services</div>
                <ul className="space-y-2 text-sm">
                    {SERVICES.slice(0, 8).map((s) => (
                        <li key={s.slug}>
                            <Link to={`/services/${s.slug}`} className="hover:text-white">
                                {s.title}
                            </Link>
                        </li>
                    ))}
                </ul>
            </div>

            <div>
                <div className="text-white font-semibold mb-3 text-sm uppercase tracking-wide">Company</div>
                <ul className="space-y-2 text-sm">
                    <li><Link to="/about" className="hover:text-white">About</Link></li>
                    <li><Link to="/areas" className="hover:text-white">Areas We Cover</Link></li>
                    <li><Link to="/reviews" className="hover:text-white">Reviews</Link></li>
                    <li><Link to="/moving-tips" className="hover:text-white">Moving Tips</Link></li>
                    <li><Link to="/faq" className="hover:text-white">FAQ</Link></li>
                    <li><Link to="/contact" className="hover:text-white">Contact</Link></li>
                    <li><Link to="/request-a-quote" className="hover:text-white">Get a Quote</Link></li>
                </ul>
            </div>

            <div>
                <div className="text-white font-semibold mb-3 text-sm uppercase tracking-wide">Legal</div>
                <ul className="space-y-2 text-sm">
                    <li><Link to="/privacy-policy" className="hover:text-white">Privacy Policy</Link></li>
                    <li><Link to="/cookie-policy" className="hover:text-white">Cookie Policy</Link></li>
                    <li><Link to="/terms" className="hover:text-white">Terms & Conditions</Link></li>
                </ul>
                <div className="text-white font-semibold mt-6 mb-3 text-sm uppercase tracking-wide">Follow</div>
                <ul className="flex flex-wrap items-center gap-3 text-sm">
                    <li>
                        <a
                            href={SOCIAL.facebook}
                            target="_blank"
                            rel="noopener noreferrer"
                            aria-label="YS Removals on Facebook"
                            className="inline-flex items-center justify-center h-9 w-9 rounded-full bg-white/10 hover:bg-white/20 transition-colors duration-200"
                        >
                            <svg viewBox="0 0 24 24" className="h-4 w-4" fill="currentColor" aria-hidden="true"><path d="M13.5 21.9v-8h2.7l.4-3.2h-3.1V8.6c0-.9.3-1.6 1.6-1.6h1.6V4.2c-.3 0-1.3-.1-2.4-.1-2.4 0-4 1.5-4 4.1v2.5H7.6v3.2h2.7v8h3.2z"/></svg>
                        </a>
                    </li>
                    <li>
                        <a
                            href={SOCIAL.instagram}
                            target="_blank"
                            rel="noopener noreferrer"
                            aria-label="YS Removals on Instagram"
                            className="inline-flex items-center justify-center h-9 w-9 rounded-full bg-white/10 hover:bg-white/20 transition-colors duration-200"
                        >
                            <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
                                <rect x="3" y="3" width="18" height="18" rx="5" />
                                <circle cx="12" cy="12" r="4" />
                                <circle cx="17.5" cy="6.5" r="1" fill="currentColor" stroke="none" />
                            </svg>
                        </a>
                    </li>
                </ul>
            </div>
        </div>
        <div className="container-ys border-t border-white/10 pt-6 pb-2 text-xs flex flex-col md:flex-row md:items-start md:justify-between gap-3">
            <div>
                &copy; {new Date().getFullYear()} {LEGAL.companyDisplayName}. All rights reserved.
            </div>
            <div className="text-white/60 leading-relaxed max-w-2xl md:text-right">
                <strong className="text-white/80 font-semibold">{LEGAL.companyDisplayName}</strong> is a company registered in {LEGAL.jurisdiction} — company number <span className="text-white/80 font-semibold">{LEGAL.companyNumber}</span>.
                <br className="hidden sm:inline" />
                Registered office: {LEGAL.registeredOffice}
            </div>
        </div>
    </footer>
);
